import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { motion } from 'framer-motion';
import { GitBranch } from 'lucide-react';

const IntentSankey = ({ filters, reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [sankeyData, setSankeyData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(true);
  const animationDuration = reducedMotion ? 0 : 0.6;

  useEffect(() => {
    fetchSankeyData();
  }, [filters]);

  const fetchSankeyData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/analytics/intent-sankey`);
      const result = await response.json();
      setSankeyData(result);
    } catch (error) {
      console.error('Error fetching intent sankey:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNodeColor = (nodeName) => {
    const colorMap = {
      'interested': '#10b981',
      'not_interested': '#ef4444',
      'callback': '#f59e0b',
      'question': '#C9A24A',
      'dnc': '#dc2626',
      'completed': '#22c55e',
      'converted': '#16a34a',
      'voicemail': '#6b7280',
      'no_answer': '#9ca3af',
      'busy': '#fbbf24'
    };
    
    const key = nodeName.toLowerCase().replace(/ /g, '_');
    return colorMap[key] || '#6366f1';
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <GitBranch className="w-5 h-5 text-gold" />
          Intent Flow
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Visualize conversation intent transitions • Top paths shown
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-pulse text-slate-400">Loading intent flow...</div>
          </div>
        ) : sankeyData.nodes.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No intent transition data available yet</p>
            <p className="text-xs mt-2">Intent flows will appear as calls are processed</p>
          </div>
        ) : (
          <motion.div
            initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: animationDuration }}
          >
            {/* Flow visualization - show top transitions */}
            <div className="space-y-4 mb-6">
              {sankeyData.links.slice(0, 10).map((link, index) => {
                const sourceNode = sankeyData.nodes.find(n => n.id === link.source);
                const targetNode = sankeyData.nodes.find(n => n.id === link.target);
                const maxValue = Math.max(...sankeyData.links.map(l => l.value));
                const widthPercent = (link.value / maxValue) * 100;
                
                return (
                  <motion.div
                    key={index}
                    className="relative"
                    initial={reducedMotion ? {} : { opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className="flex items-center gap-4">
                      {/* Source */}
                      <div 
                        className="px-4 py-2 rounded-lg font-medium text-sm"
                        style={{ 
                          backgroundColor: getNodeColor(sourceNode?.name || ''),
                          color: '#ffffff',
                          minWidth: '120px',
                          textAlign: 'center'
                        }}
                      >
                        {sourceNode?.name || link.source}
                      </div>
                      
                      {/* Arrow with value */}
                      <div className="flex-1 relative h-8">
                        <div 
                          className="absolute top-1/2 transform -translate-y-1/2 h-2 rounded-full bg-gold"
                          style={{ width: `${widthPercent}%` }}
                        >
                        </div>
                        <div className="absolute top-1/2 right-4 transform -translate-y-1/2 bg-slate-800 px-2 py-1 rounded text-xs font-bold text-gold">
                          {link.value}
                        </div>
                      </div>
                      
                      {/* Target */}
                      <div 
                        className="px-4 py-2 rounded-lg font-medium text-sm"
                        style={{ 
                          backgroundColor: getNodeColor(targetNode?.name || ''),
                          color: '#ffffff',
                          minWidth: '120px',
                          textAlign: 'center'
                        }}
                      >
                        {targetNode?.name || link.target}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-6 flex flex-wrap gap-3 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#10b981' }}></div>
                <span className="text-slate-400">Positive Intent</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#ef4444' }}></div>
                <span className="text-slate-400">Negative Intent</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#f59e0b' }}></div>
                <span className="text-slate-400">Follow-up</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#6b7280' }}></div>
                <span className="text-slate-400">No Contact</span>
              </div>
            </div>

            {/* Stats */}
            {sankeyData.meta && (
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gold">{sankeyData.meta.unique_intents || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Unique Intents</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-ink-500">{sankeyData.links?.length || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Transitions</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-400">{sankeyData.meta.sample_size || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Total Calls</p>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};

export default IntentSankey;
